import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/errors/use-toast';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [email, setEmail] = useState('');
  
  const token = searchParams.get('token');
  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type');
  const next = searchParams.get('next') || '/project-manager';
  
  // Also check hash params (Supabase sometimes puts params there)
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const hashError = hashParams.get('error');
  const hashErrorCode = hashParams.get('error_code');
  const hashErrorDescription = hashParams.get('error_description');
  const hashToken = hashParams.get('token');
  const hashTokenHash = hashParams.get('token_hash');
  const hashType = hashParams.get('type');
  
  // Use token from hash if not in search params
  const finalToken = token || hashToken;
  const finalTokenHash = tokenHash || hashTokenHash;
  const finalType = type || hashType || 'invite';
  
  // Debug logging
  console.log('AcceptInvite - URL params:', {
    token,
    tokenHash,
    type,
    next,
    fullURL: window.location.href,
    searchParams: searchParams.toString(),
    hashParams: window.location.hash,
    hashError,
    hashErrorCode,
    hashErrorDescription,
    finalToken,
    finalTokenHash,
    finalType
  });
  
  useEffect(() => {
    verifyInvite();
  }, []);
  
  const verifyInvite = async () => {
    console.log('AcceptInvite - Starting verification...');
    
    // Check for hash error first
    if (hashError === 'access_denied' && hashErrorCode === 'otp_expired') {
      console.error('AcceptInvite - Invite link expired or invalid');
      toast({
        title: 'Invite Link Expired',
        description: 'This invite link has expired or has already been used. Please request a new invitation.',
        variant: 'destructive'
      });
      setTimeout(() => navigate('/signin'), 3000);
      return;
    }
    
    try {
      // Check if we have access_token in the hash (Supabase returns it there after invite verification)
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (accessToken && refreshToken) {
        console.log('AcceptInvite - Found tokens in hash, setting session...');
        
        // Set the session using the tokens from the hash
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (sessionError) {
          console.error('AcceptInvite - Failed to set session:', sessionError);
          throw sessionError;
        }
        
        console.log('AcceptInvite - Session set successfully:', {
          userEmail: sessionData.session?.user?.email,
          userMetadata: sessionData.session?.user?.user_metadata
        });
        
        // Clear the hash to clean up the URL
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      
      // Wait a moment for session to be established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the current session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      console.log('AcceptInvite - Session check:', {
        hasSession: !!session,
        sessionError: error,
        userEmail: session?.user?.email,
        userId: session?.user?.id,
        confirmedAt: session?.user?.confirmed_at,
        userMetadata: session?.user?.user_metadata,
        emailConfirmedAt: session?.user?.email_confirmed_at,
        lastSignInAt: session?.user?.last_sign_in_at
      });
      
      if (error) {
        console.error('AcceptInvite - Session error:', error);
        throw error;
      }
      
      if (session?.user?.email) {
        setEmail(session.user.email);
        
        // Check if user has completed setup (has users record)
        const { data: profile } = await supabase
          .from('users')
          .select('id')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          // User has already completed setup
          console.log('AcceptInvite - User already set up, redirecting to:', next);
          navigate(next);
          return;
        }
        
        // User needs to complete setup
        console.log('AcceptInvite - New invited user needs to complete setup');
        setIsVerifying(false);
        return;
      }
      
      // No session - this shouldn't happen after Supabase redirect
      console.error('AcceptInvite - No session after Supabase redirect');
      throw new Error('Failed to establish session. Please try the invite link again.');
      
    } catch (error: any) {
      console.error('Failed to verify invite:', error);
      toast({
        title: 'Invalid Invite',
        description: error.message || 'This invite link is invalid or has expired.',
        variant: 'destructive'
      });
      setTimeout(() => navigate('/signin'), 3000);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('AcceptInvite - Form submitted, validating passwords...');
    
    // Validate passwords
    if (password.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // At this point, user should already have a session from Supabase redirect
      // We just need to update their password
      console.log('AcceptInvite - Updating user password...');
      
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });
      
      console.log('AcceptInvite - Password update result:', {
        success: !updateError,
        error: updateError
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Get the current user after password update
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Failed to get user information');
      }
      
      console.log('AcceptInvite - Initializing user profile for:', user.email);
      
      // Initialize user profile (this triggers credit allocation)
      const { data: profileData, error: profileError } = await supabase.functions.invoke(
        'initialize-user-profile',
        {
          body: {
            userId: user.id,
            email: user.email
          }
        }
      );
      
      if (profileError) {
        console.error('AcceptInvite - Failed to initialize profile:', profileError);
        // Don't throw - continue anyway as the trigger might handle it
      } else {
        console.log('AcceptInvite - Profile initialized successfully:', profileData);
      }
      
      toast({
        title: 'Welcome to FinalPiece!',
        description: 'Your account has been set up with 30 free credits. Let\'s personalize your profile.',
        variant: 'default'
      });
      
      // Redirect to welcome/onboarding page to complete account setup
      navigate('/welcome');
    } catch (error: any) {
      console.error('Failed to set password:', error);
      toast({
        title: 'Setup Failed',
        description: error.message || 'Failed to set up your account. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isVerifying) {
    return (
      <AuthLayout
        title="Verifying Invitation"
        description="Please wait while we verify your invitation"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Verifying your invitation...</p>
        </div>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout
      title="Set Your Password"
      description="Welcome! Please set a password for your account."
    >
      <div className="w-full space-y-6">
        {email && (
          <p className="text-sm text-center text-muted-foreground">
            Setting up account for: <span className="font-medium">{email}</span>
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                disabled={isLoading}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting up account...
              </>
            ) : (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Set Password & Continue
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            By setting your password, you agree to our Terms of Service and Privacy Policy
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default AcceptInvite;